import React, { useState } from 'react';
import { Save, Tag, DollarSign, TrendingUp, AlertCircle } from 'lucide-react';
const initialServices = [{
  id: 'h1',
  category: 'Hosting Packages',
  name: 'Basic Hosting',
  base: 15,
  retail: 35
}, {
  id: 'h2',
  category: 'Hosting Packages',
  name: 'Professional Hosting',
  base: 25,
  retail: 65
}, {
  id: 'h3',
  category: 'Hosting Packages',
  name: 'Enterprise Hosting',
  base: 45,
  retail: 99
}, {
  id: 'd1',
  category: 'Domains',
  name: '.com Registration',
  base: 10,
  retail: 15
}, {
  id: 'd2',
  category: 'Domains',
  name: '.com Transfer',
  base: 10,
  retail: 15
}, {
  id: 'd3',
  category: 'Domains',
  name: '.ca Registration',
  base: 9,
  retail: 14
}, {
  id: 'd4',
  category: 'Domains',
  name: '.net Registration',
  base: 12,
  retail: 18
}, {
  id: 'a1',
  category: 'AVA Suite',
  name: 'AVA Sentinel (Monitoring)',
  base: 5,
  retail: 15
}, {
  id: 'a2',
  category: 'AVA Suite',
  name: 'AVA Finance',
  base: 10,
  retail: 29
}, {
  id: 'a3',
  category: 'AVA Suite',
  name: 'AVA CRM',
  base: 15,
  retail: 49
}, {
  id: 's1',
  category: 'Digital Services',
  name: 'Digital Marketing (Monthly)',
  base: 150,
  retail: 300
}, {
  id: 's2',
  category: 'Digital Services',
  name: 'Graphic Design Retainer',
  base: 100,
  retail: 250
}, {
  id: 's3',
  category: 'Digital Services',
  name: 'Custom Page Development',
  base: 200,
  retail: 500
}, {
  id: 's4',
  category: 'Digital Services',
  name: 'SEO Audit (One-time)',
  base: 50,
  retail: 150
}, {
  id: 'o1',
  category: 'Add-ons',
  name: 'Premium SSL Certificate',
  base: 20,
  retail: 50
}, {
  id: 'o2',
  category: 'Add-ons',
  name: 'Business Email Hosting',
  base: 2,
  retail: 6
}, {
  id: 'o3',
  category: 'Add-ons',
  name: 'Global CDN Premium',
  base: 8,
  retail: 20
}];
export function Pricing() {
  const [services, setServices] = useState(initialServices);
  const handlePriceChange = (id: string, newRetail: string) => {
    const val = parseFloat(newRetail);
    if (isNaN(val)) return;
    setServices(services.map(s => s.id === id ? {
      ...s,
      retail: val
    } : s));
  };
  const calculateMargin = (base: number, retail: number) => {
    if (retail <= 0) return 0;
    return ((retail - base) / retail * 100).toFixed(1);
  };
  const categories = Array.from(new Set(services.map(s => s.category)));
  // Calculate potential monthly revenue based on some mock volume
  const potentialRevenue = services.reduce((acc, s) => acc + (s.retail - s.base) * 10, 0);
  return <div className="space-y-8 max-w-[1600px] mx-auto" data-id="element-1761">
      <div className="flex items-center justify-between bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-1762">
        <div data-id="element-1763">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-1764">
            Pricing Strategy
          </h1>
          <p className="text-sm text-gray-500 font-medium" data-id="element-1765">
            Set retail prices for all your storefront services and calculate
            margins.
          </p>
        </div>
        <button className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-500/20 transition-all flex items-center gap-2" data-id="element-1766">
          <Save size={18} data-id="element-1767" />
          Save All Prices
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-1768">
        {/* Left Column: Summary */}
        <div className="lg:col-span-1 space-y-6" data-id="element-1769">
          <div className="bg-slate-900 p-6 rounded-lg text-white shadow-sm" data-id="element-1770">
            <div className="flex items-center gap-3 mb-4" data-id="element-1771">
              <div className="p-2 bg-indigo-500/20 text-indigo-400 rounded-lg" data-id="element-1772">
                <TrendingUp size={20} data-id="element-1773" />
              </div>
              <h3 className="font-bold" data-id="element-1774">Profit Potential</h3>
            </div>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-1" data-id="element-1775">
              Estimated Monthly Margin
            </p>
            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1776">
              ${potentialRevenue.toLocaleString()}
            </h2>
            <p className="text-xs text-slate-400 leading-relaxed" data-id="element-1777">
              Based on an average volume of 10 sales per service category.
              Adjust your retail prices to see how it impacts your bottom line.
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-100 p-5 rounded-lg flex gap-3" data-id="element-1778">
            <AlertCircle size={18} className="text-blue-600 shrink-0 mt-0.5" data-id="element-1779" />
            <p className="text-xs text-blue-800 leading-relaxed font-medium" data-id="element-1780">
              Prices updated here will immediately reflect on your live
              storefront and checkout pages. Base costs are fixed by
              NiishEngine.
            </p>
          </div>
        </div>

        {/* Right Column: Pricing Tables */}
        <div className="lg:col-span-3 space-y-8" data-id="element-1781">
          {categories.map(category => <div key={category} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-1782">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center gap-2" data-id="element-1783">
                <Tag size={16} className="text-gray-400" data-id="element-1784" />
                <h3 className="font-bold text-slate-900" data-id="element-1785">{category}</h3>
              </div>
              <table className="w-full text-left border-collapse" data-id="element-1786">
                <thead data-id="element-1787">
                  <tr className="text-[10px] font-bold uppercase tracking-wider text-gray-400 border-b border-gray-100" data-id="element-1788">
                    <th className="px-6 py-4 w-1/3" data-id="element-1789">Service Name</th>
                    <th className="px-6 py-4" data-id="element-1790">Base Cost</th>
                    <th className="px-6 py-4" data-id="element-1791">Retail Price</th>
                    <th className="px-6 py-4 text-right" data-id="element-1792">Margin</th>
                  </tr>
                </thead>
                <tbody className="text-sm" data-id="element-1793">
                  {services.filter(s => s.category === category).map(service => {
                const margin = parseFloat(calculateMargin(service.base, service.retail));
                const isLowMargin = margin < 20;
                return <tr key={service.id} className="hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0" data-id="element-1794">
                          <td className="px-6 py-4 font-bold text-slate-900" data-id="element-1795">
                            {service.name}
                          </td>
                          <td className="px-6 py-4 text-gray-500 font-medium" data-id="element-1796">
                            ${service.base.toFixed(2)}
                          </td>
                          <td className="px-6 py-4" data-id="element-1797">
                            <div className="relative max-w-[120px]" data-id="element-1798">
                              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} data-id="element-1799" />
                              <input type="number" value={service.retail} onChange={e => handlePriceChange(service.id, e.target.value)} className="w-full pl-8 pr-3 py-2 bg-white border border-gray-200 rounded-md text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all" data-id="element-1800" />
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right" data-id="element-1801">
                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${isLowMargin ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`} data-id="element-1802">
                              {margin}%
                            </span>
                          </td>
                        </tr>;
              })}
                </tbody>
              </table>
            </div>)}
        </div>
      </div>
    </div>;
}